import { loadRnnoise, RnnoiseWorkletNode } from '@sapphi-red/web-noise-suppressor';
import type { AudioProcessorOptions, Track, TrackProcessor } from 'livekit-client';

const ASSETS_PATH = '/rnnoise/';
const RNNOISE_SAMPLE_RATE = 48_000;
const CONTEXT_RESUME_TIMEOUT_MS = 2000;

let wasmBinary: Promise<ArrayBuffer> | undefined;

export class AudioContextNotRunningError extends Error {
    constructor(state: string) {
        super(`Noise suppression audio context is ${state}`);
        this.name = 'AudioContextNotRunningError';
    }
}

async function fetchWasm() {
    const binary = await loadRnnoise({
        url: `${ASSETS_PATH}rnnoise.wasm`,
        simdUrl: `${ASSETS_PATH}rnnoise_simd.wasm`,
    });
    if (!WebAssembly.validate(binary)) throw new Error('RNNoise wasm is not valid WebAssembly');
    return binary;
}

function loadWasm() {
    wasmBinary ??= fetchWasm().catch((error: unknown) => {
        wasmBinary = undefined;
        throw error;
    });
    return wasmBinary;
}

async function resumeContext(context: AudioContext) {
    await Promise.race([
        context.resume().catch(() => undefined),
        new Promise((resolve) => {
            setTimeout(resolve, CONTEXT_RESUME_TIMEOUT_MS);
        }),
    ]);
    if (context.state !== 'running') throw new AudioContextNotRunningError(context.state);
}

export class RnnoiseProcessor implements TrackProcessor<Track.Kind.Audio, AudioProcessorOptions> {
    readonly name = 'rnnoise';
    processedTrack: MediaStreamTrack | undefined;
    private readonly onContextLost: () => void;
    private context: AudioContext | undefined;
    private denoiser: RnnoiseWorkletNode | undefined;
    private source: MediaStreamAudioSourceNode | undefined;

    constructor(onContextLost: () => void) {
        this.onContextLost = onContextLost;
    }

    async init({ track }: AudioProcessorOptions) {
        const context = new AudioContext({ sampleRate: RNNOISE_SAMPLE_RATE, latencyHint: 'interactive' });
        this.context = context;
        try {
            const [binary] = await Promise.all([
                loadWasm(),
                context.audioWorklet.addModule(`${ASSETS_PATH}rnnoise-worklet.js`),
                resumeContext(context),
            ]);

            const denoiser = new RnnoiseWorkletNode(context, { maxChannels: 1, wasmBinary: binary });
            denoiser.channelCount = 1;
            denoiser.channelCountMode = 'explicit';
            const destination = context.createMediaStreamDestination();
            destination.channelCount = 1;
            denoiser.connect(destination);
            this.denoiser = denoiser;
            this.connectSource(track);
            this.processedTrack = destination.stream.getAudioTracks()[0];
            context.addEventListener('statechange', this.handleStateChange);
        } catch (error) {
            await this.destroy();
            throw error;
        }
    }

    restart({ track }: AudioProcessorOptions) {
        this.connectSource(track);
        return Promise.resolve();
    }

    async destroy() {
        this.source?.disconnect();
        this.source = undefined;
        this.denoiser?.disconnect();
        this.denoiser?.destroy();
        this.denoiser = undefined;
        const context = this.context;
        this.context = undefined;
        if (!context) return;
        context.removeEventListener('statechange', this.handleStateChange);
        if (context.state !== 'closed') await context.close();
    }

    private connectSource(track: MediaStreamTrack) {
        const { context, denoiser } = this;
        if (!context || !denoiser) return;
        this.source?.disconnect();
        this.source = context.createMediaStreamSource(new MediaStream([track]));
        this.source.connect(denoiser);
    }

    private readonly handleStateChange = () => {
        const context = this.context;
        if (!context || context.state === 'running' || context.state === 'closed') return;
        resumeContext(context).catch(() => {
            if (this.context === context) this.onContextLost();
        });
    };
}
